import { useState, useEffect, useCallback } from 'react';
import { AuthenticationStorage } from '../utils';
import { STORAGE_KEYS, AUTH_STORAGE_KEYS } from '../constants';
import type { EnvironmentValue } from '../constants';

/**
 * Generic hook that automatically syncs with storage changes
 * Based on the pattern from Core module's useGlobalMetadataLanguageOverride
 */
function useStorageState<T>(storageKey: string, getter: () => T): T {
  const [value, setValue] = useState<T>(getter);

  useEffect(() => {
    // Initialize with current storage value
    setValue(getter());

    const handleStorageChange = (event: Event) => {
      if (event instanceof StorageEvent) {
        // Cross-window storage event (standard browser behavior)
        if (event.key?.includes(storageKey)) {
          setValue(getter());
        }
      } else if (event instanceof CustomEvent) {
        // Same-window custom event (our enhancement)
        if (event.detail?.key === storageKey) {
          setValue(getter());
        }
      }
    };

    // Listen for both standard storage events and our custom events
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-storage-change', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-storage-change', handleStorageChange);
    };
  }, [storageKey, getter]);

  return value;
}

/**
 * Hook for managing redirect URL with automatic storage sync
 * Survives external redirects (IDP, ACSR) and cross-window communication
 */
export function useRedirectUrl(): [
  string | null,
  (url: string | null) => void
] {
  const redirectUrl = useStorageState(
    STORAGE_KEYS.AUTHENTICATION_REDIRECT,
    () => AuthenticationStorage.getRedirectUrl()
  );

  const setRedirectUrl = useCallback((url: string | null) => {
    if (url) {
      AuthenticationStorage.setRedirectUrl(url);
    } else {
      AuthenticationStorage.removeRedirectUrl();
    }
  }, []);

  return [redirectUrl, setRedirectUrl];
}

/**
 * Hook for managing session data with automatic storage sync
 * Provides reactive access to session and system IDs
 */
export function useSessionData(): {
  sessionId: string | null;
  systemId: string | null;
  setSessionId: (id: string | null) => void;
  setSystemId: (id: string | null) => void;
} {
  const sessionId = useStorageState(STORAGE_KEYS.SESSION_ID, () =>
    AuthenticationStorage.getSessionId()
  );

  const systemId = useStorageState(STORAGE_KEYS.SYSTEM_ID, () =>
    AuthenticationStorage.getSystemId()
  );

  const setSessionId = useCallback((id: string | null) => {
    if (id) {
      AuthenticationStorage.setSessionId(id);
    } else {
      AuthenticationStorage.removeSessionId();
    }
  }, []);

  const setSystemId = useCallback((id: string | null) => {
    if (id) {
      AuthenticationStorage.setSystemId(id);
    } else {
      AuthenticationStorage.removeSystemId();
    }
  }, []);

  return {
    sessionId,
    systemId,
    setSessionId,
    setSystemId
  };
}

/**
 * Hook for managing user preferences with automatic storage sync
 * Handles username and environment selection for development convenience
 */
export function useUserPreferences(): {
  lastUsername: string | null;
  selectedEnvironment: EnvironmentValue | null;
  setLastUsername: (username: string | null) => void;
  setSelectedEnvironment: (env: EnvironmentValue | null) => void;
} {
  const lastUsername = useStorageState(AUTH_STORAGE_KEYS.LAST_USERNAME, () =>
    AuthenticationStorage.getLastUsername()
  );

  const selectedEnvironment = useStorageState(STORAGE_KEYS.ENVIRONMENT, () =>
    AuthenticationStorage.getSelectedEnvironment()
  );

  const setLastUsername = useCallback((username: string | null) => {
    if (username) {
      AuthenticationStorage.setLastUsername(username);
    } else {
      AuthenticationStorage.removeLastUsername();
    }
  }, []);

  const setSelectedEnvironment = useCallback((env: EnvironmentValue | null) => {
    if (env) {
      AuthenticationStorage.setSelectedEnvironment(env);
    } else {
      AuthenticationStorage.removeSelectedEnvironment();
    }
  }, []);

  return {
    lastUsername,
    selectedEnvironment,
    setLastUsername,
    setSelectedEnvironment
  };
}
